import API_URL from '../config';
import { getAuthHeaders } from '../utils/api';
import { getLocalDateString } from '../utils/formatters';

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getProfile = async () => {
  const response = await fetch(`${API_URL}/api/users/me`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch profile');
  }

  return response.json();
};

/**
 * Update user profile
 * @param {Object} profileData - { firstName?, lastName?, displayName?, bio?, avatar? }
 * @returns {Promise<Object>} Updated user data
 */
export const updateProfile = async (profileData) => {
  const response = await fetch(`${API_URL}/api/users/me`, {
    method: 'PUT',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(profileData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update profile');
  }

  return response.json();
};

/**
 * Change password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Success message
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await fetch(`${API_URL}/api/users/me/password`, {
    method: 'PUT',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to change password');
  }

  return response.json();
};

/**
 * Delete account
 * @param {string} password - Password confirmation
 * @returns {Promise<Object>} Success message
 */
export const deleteAccount = async (password) => {
  const response = await fetch(`${API_URL}/api/users/me`, {
    method: 'DELETE',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete account');
  }

  return response.json();
};

/**
 * Get user stats
 * @returns {Promise<Object>} User stats including completions, totals, and averages
 */
export const getUserStats = async () => {
  const localDate = getLocalDateString();
  const response = await fetch(`${API_URL}/api/users/me/stats?date=${localDate}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch stats');
  }

  return response.json();
};

/**
 * Get public user profile by username
 * @param {string} username - Username to look up
 * @returns {Promise<Object|null>} Public profile data, or null if not found
 */
export const getPublicProfile = async (username) => {
  const response = await fetch(`${API_URL}/api/users/${encodeURIComponent(username)}`);

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch profile');
  }

  return response.json();
};

/**
 * Get user badges
 * @returns {Promise<Object>} Badge definitions and earned badges
 */
export const getBadges = async () => {
  const response = await fetch(`${API_URL}/api/users/me/badges`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch badges');
  }

  return response.json();
};
